import React, { useCallback, useEffect, useState } from 'react';
import styles from './styles.module.scss';
import Loader from '../../common/Loader';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import { useDispatch } from 'react-redux';
import {
  addAvatarRequest,
  deleteAvatarRequest,
  get_user_info,
  getMyProfileRequest
} from '../../../helpers/api';
import Compressor from 'compressorjs';
import { TextField, Checkbox, FormControlLabel } from '@mui/material';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { routes } from '../../../assets/routes';
import AvatarReco from '../../common/AvatarReco';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import '@hassanmojab/react-modern-calendar-datepicker/lib/DatePicker.css';
import { Backdrop } from '@material-ui/core';
import { addExtraBackButtonControls } from '../../../helpers/utils';
import classNames from 'classnames';
import { toggleBackButtonAction } from '../../../reactRedux/Actions/sideBarAction';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import Cropper from 'react-easy-crop';
import { getCroppedImg, readFile } from '../../../helpers/utils.js';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';
import { useStyles } from '../../../assets/style/StylesCrop';
import { useMutation, useQuery } from '@tanstack/react-query';
import { editProfileRequest } from './api';
import './styles.scss';
import { Controller, useForm } from 'react-hook-form';
import DatePicker from './DatePicker';
import ListInput from './ListInput';
import { useTranslation } from '../../../i18n/LanguageProvider';

const EditProfile = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const location = useLocation();

  const setSideBarBackButton = (show) => dispatch(toggleBackButtonAction(show));
  const isDesktop = useDesktopDisplay();

  const [userId] = useState(get_user_info().id);
  const fromWelcomePage = location.state ? location.state.welcomePage : false;
  const [avatar, setAvatar] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageSrc, setImageSrc] = React.useState(null);
  const [avatarName, setAvatarName] = React.useState(null);
  const { handleSubmit, control } = useForm();

  const { data, isLoading } = useQuery(['myProfile'], getMyProfileRequest, {
    onError(err) {
      toast.error(err.response?.data?.detail);
    },
    onSuccess(response) {
      setAvatar(response.avatar);
    }
  });

  const addAvatarMutation = useMutation(addAvatarRequest, {
    onSuccess({ data: { avatar } }) {
      setAvatar(avatar);
      setImageSrc(null);
      toast.success(t('editProfile.avatarChanged'));
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const deleteAvatarMutation = useMutation(deleteAvatarRequest, {
    onSuccess() {
      toast.success(t('editProfile.avatarDeleted'));
      setImageSrc(null);
      setAvatar(null);
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const editProfileMutation = useMutation(editProfileRequest, {
    onSuccess() {
      toast.success(t('editProfile.saved'));
      history.push(generatePath(routes.PROFILE, { id: userId }));
    },
    onError(err) {
      toast.error(err.response?.data?.detail);
    }
  });

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);
  const setAvatarImage = useCallback(async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);

      const dataURLtoFile = (dataUrl, filename) => {
        let arr = dataUrl.split(','),
          mime = arr[0].match(/:(.*?);/)[1],
          bstr = atob(arr[1]),
          n = bstr.length,
          u8arr = new Uint8Array(n);

        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }

        return new File([u8arr], filename, { type: mime });
      };

      let file = dataURLtoFile(croppedImage, avatarName);

      new Compressor(file, {
        quality: 0.5,
        success(result) {
          const formData = new FormData();

          // The third parameter is required for server
          formData.append('file', result, result.name);

          addAvatarMutation.mutate({ file });
        },
        error(err) {
          console.log(err.message);
        }
      });
    } catch (e) {
      toast.error(t('editProfile.saveError'));
    }
  }, [imageSrc, croppedAreaPixels]);

  const onClose = useCallback(() => {
    setImageSrc(null);
  }, []);
  const onAvatarChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setAvatarName(e.target.files[0].name);
      let imageDataUrl = await readFile(file);

      setImageSrc(imageDataUrl);
    }
  };

  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));

  useEffect(() => {
    setSideBarBackButton(true);

    return () => setSideBarBackButton(false);
  }, []);

  useEffect(() => {
    addExtraBackButtonControls({
      data: showCalendar,
      func: () => setShowCalendar(false)
    });

    if (document.getElementById('birthday')) {
      document.getElementById('birthday').blur();
    }
    return () => addExtraBackButtonControls({});
  }, [showCalendar]);

  useEffect(() => {
    setHeader(t('editProfile.title'));
    setHeaderConfigs({ credits: false, exit: false, back: true });
  }, []);

  return (
    <div className={classNames(styles.page, styles.full)}>
      <Helmet>
        <title>{t('editProfile.title')}</title>
      </Helmet>
      {isDesktop && (
        <div className={styles.pageHeader}>{t('editProfile.title')}</div>
      )}
      {isLoading ? (
        <Loader size={40} />
      ) : (
        <form
          onSubmit={handleSubmit((data) => {
            editProfileMutation.mutate(data);
          })}
        >
          <div className={styles.wrapper}>
            <div className={styles.section} style={{ flexBasis: '100%' }}>
              <div className={styles.avatar}>
                <AvatarReco size={10} avatar={avatar} />
              </div>
              <div className={styles.add_delete}>
                <Button variant="text" component="label">
                  <input type="file" hidden onChange={onAvatarChange} />
                  {t('editProfile.choosePhoto')}
                </Button>
                <Button
                  variant="text"
                  onClick={() => deleteAvatarMutation.mutate({ id: avatar.id })}
                  style={{ color: '#FF0000' }}
                >
                  {t('common.delete')}
                </Button>
              </div>
            </div>
            <div className={classNames(styles.section, styles.editParts)}>
              <div className={styles.sectionTop}>
                <h4>{t('editProfile.personalInfo')}</h4>
              </div>
              <div className={styles.sectionBody}>
                <Controller
                  name="about"
                  control={control}
                  defaultValue={data?.about}
                  render={({ field }) => (
                    <TextField
                      multiline
                      rows={4}
                      label={t('editProfile.about')}
                      {...field}
                    />
                  )}
                />

                <Controller
                  render={({ field }) => (
                    <DatePicker label={t('editProfile.birthday')} {...field} />
                  )}
                  name="birthday"
                  control={control}
                  defaultValue={data?.birthday}
                />
                <Controller
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="show_my_birthday"
                          defaultChecked={data?.show_my_birthday}
                          {...field}
                        />
                      }
                      label={t('editProfile.showBirthYear')}
                    />
                  )}
                  name="show_my_birthday"
                  control={control}
                  defaultValue={data?.show_my_birthday}
                />

                <Controller
                  render={({ field }) => (
                    <ListInput
                      comment={t('editProfile.emptyWishes')}
                      label={t('profile.wishes')}
                      {...field}
                    />
                  )}
                  name="wishes"
                  control={control}
                  defaultValue={data?.wishes}
                />

                <Controller
                  render={({ field }) => (
                    <ListInput
                      comment={t('editProfile.emptyFavorites')}
                      label={t('profile.favorites')}
                      {...field}
                    />
                  )}
                  name="favorites"
                  control={control}
                  defaultValue={data?.favorites}
                />

                <Controller
                  render={({ field }) => (
                    <ListInput
                      comment={t('editProfile.emptySkills')}
                      label={t('profile.skills')}
                      {...field}
                    />
                  )}
                  name="skills"
                  control={control}
                  defaultValue={data?.skills}
                />
              </div>
            </div>

            <div className={classNames(styles.section, styles.editParts)}>
              <div className={styles.sectionTop}>
                <h4>{t('editProfile.contacts')}</h4>
              </div>
              <div className={styles.sectionBody}>
                <Controller
                  name="linkedin"
                  control={control}
                  defaultValue={data?.linkedin}
                  render={({ field }) => (
                    <TextField
                      name="linkedin"
                      label={t('editProfile.linkedin')}
                      ltr
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="whatsapp"
                  control={control}
                  defaultValue={data?.whatsapp}
                  render={({ field }) => (
                    <TextField
                      className={styles.input}
                      name="whatsapp"
                      label={t('editProfile.whatsapp')}
                      ltr
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="telegram"
                  control={control}
                  defaultValue={data?.telegram}
                  render={({ field }) => (
                    <TextField
                      className={styles.input}
                      name="telegram"
                      label={t('editProfile.telegram')}
                      ltr
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="skype"
                  control={control}
                  defaultValue={data?.skype}
                  render={({ field }) => (
                    <TextField
                      className={styles.input}
                      name="skype"
                      label={t('editProfile.skype')}
                      ltr
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="phone"
                  control={control}
                  defaultValue={data?.phone}
                  render={({ field }) => (
                    <TextField
                      className={styles.input}
                      name="phone"
                      label={t('editProfile.phone')}
                      ltr
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="gmail"
                  control={control}
                  defaultValue={data?.gmail}
                  render={({ field }) => (
                    <TextField
                      className={styles.input}
                      name="gmail"
                      label={t('editProfile.email')}
                      isLtr
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="instagram"
                  control={control}
                  defaultValue={data?.instagram}
                  render={({ field }) => (
                    <TextField
                      className={styles.input}
                      name="instagram"
                      label={t('editProfile.instagram')}
                      ltr
                      {...field}
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <div className={styles.btnWrapper}>
            <Button
              variant="contained"
              onClick={handleSubmit((data) => {
                editProfileMutation.mutate(data);
              })}
              type="submit"
              loading={editProfileMutation.isLoading}
              disabled={editProfileMutation.isLoading}
              fullWidth
            >
              {t('editProfile.save')}
            </Button>
            <Button
              fullWidth
              onClick={() => {
                if (fromWelcomePage) {
                  history.push(routes.HOME);
                } else {
                  history.goBack();
                }
              }}
              disabled={editProfileMutation.isLoading}
              variant="outlined"
            >
              {t('common.cancel')}
            </Button>
          </div>
          {imageSrc ? (
            <>
              <Backdrop
                mountOnEnter
                unmountOnExit
                style={{
                  zIndex: 99999,
                  backgroundColor: 'rgba(0, 0, 0, 0.7 )'
                }}
                open={imageSrc}
              >
                <Container maxWidth="xs">
                  <Box
                    p="5px"
                    borderRadius={4}
                    className={classes.containerCrop}
                  >
                    <div className={classes.cropContainer}>
                      <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        objectFit="horizontal-cover"
                        aspect={1 / 1}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                      />
                    </div>
                    <div className={classes.controlCrop}>
                      <div className={classes.sliderContainer}>
                        <Typography
                          id="zoom"
                          gutterBottom
                          variant="overline"
                          classes={{ root: classes.sliderLabel }}
                        >
                          {t('editProfile.zoom')}
                        </Typography>
                        <Slider
                          color="secondary"
                          value={zoom}
                          min={1}
                          max={3}
                          step={0.1}
                          aria-labelledby="Zoom"
                          classes={{ root: classes.slider }}
                          onChange={(e, zoom) => setZoom(zoom)}
                        />
                      </div>
                      <div className={classes.sliderContainer}></div>
                      <Button
                        onClick={setAvatarImage}
                        variant="contained"
                        size="small"
                        disabled={addAvatarMutation.isLoading}
                        style={{ marginInlineEnd: '16px' }}
                      >
                        {addAvatarMutation.isLoading ? (
                          <Loader size={20} />
                        ) : (
                          t('common.confirm')
                        )}
                      </Button>
                      <Button
                        onClick={onClose}
                        size="small"
                        variant="outlined"
                        disabled={addAvatarMutation.isLoading}
                      >
                        {t('common.cancel')}
                      </Button>
                    </div>
                  </Box>
                </Container>
              </Backdrop>
            </>
          ) : null}
        </form>
      )}
    </div>
  );
};

export default EditProfile;
